import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Button, Form, Icon, IconButton,
} from '@openedx/paragon';
import { Close } from '@openedx/paragon/icons';
import PropTypes from 'prop-types';

import validateUsername from './validator';
import { FormGroup } from '../../../common-components';
import {
  clearRegistrationBackendError,
  clearUsernameSuggestions,
  fetchRealtimeValidations,
} from '../../data/actions';
import messages from '../../messages';

/**
 * Username field wrapper. It accepts following handlers
 * - handleChange for setting value change and
 * - handleErrorChange for setting error
 *
 * It is responsible for
 * - Rendering username suggestions
 * - Setting and clearing username suggestions
 * - Performing username field validations
 * - clearing error on focus
 * - setting value on change
 */
const UsernameField = (props) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const {
    value,
    errorMessage,
    handleChange,
    handleErrorChange,
  } = props;

  const usernameSuggestions = useSelector(state => state.register.usernameSuggestions);
  const validationApiRateLimited = useSelector(state => state.register.validationApiRateLimited);
  const showSuggestions = usernameSuggestions.length > 0 && !value;

  const handleOnBlur = (event) => {
    const { value: username } = event.target;
    const fieldError = validateUsername(username, formatMessage);
    if (fieldError) {
      handleErrorChange('username', fieldError);
    } else if (!validationApiRateLimited) {
      dispatch(fetchRealtimeValidations({ username }));
    }
  };

  const handleOnChange = (event) => {
    let username = event.target.value;
    if (username.length > 30) {
      return;
    }
    if (event.target.value.startsWith(' ')) {
      username = username.trim();
    }
    handleChange({ target: { name: 'username', value: username } });
  };

  const handleOnFocus = (event) => {
    dispatch(clearUsernameSuggestions());
    if (event?.target?.value === ' ') {
      handleChange({ target: { name: 'username', value: '' } });
    }
    handleErrorChange('username', '');
    dispatch(clearRegistrationBackendError('username'));
  };

  const handleSuggestionClick = (event, suggestion = '') => {
    event.preventDefault();
    handleErrorChange('username', '');
    handleChange({ target: { name: 'username', value: suggestion } });
    dispatch(clearUsernameSuggestions());
  };

  const handleUsernameSuggestionClose = () => {
    dispatch(clearUsernameSuggestions());
  };

  if (showSuggestions) {
    const labelText = props.floatingLabel || formatMessage(messages['registration.username.label']);
    return (
      <Form.Group className="username__form-group-wrapper" isInvalid={!!errorMessage}>
        <Form.Label htmlFor="username">{labelText}</Form.Label>
        <div className="username-suggestions" role="listbox">
          <span className="username-suggestion--label">
            {formatMessage(messages['registration.username.suggestion.label'])}
          </span>
          <div className="username-scroll-suggested--form-field">
            {usernameSuggestions.map((username, index) => (
              <Button
                type="button"
                name="username"
                variant="ghost-secondary"
                className="username-suggestions--chip data-hj-suppress"
                autoComplete={props.autoComplete}
                key={`suggestion-${index.toString()}`}
                tabIndex={0}
                onClick={(e) => handleSuggestionClick(e, username)}
                role="option"
              >
                {username}
              </Button>
            ))}
          </div>
          <IconButton
            src={Close}
            iconAs={Icon}
            alt={formatMessage(messages['registration.username.suggestion.close.alt'])}
            onClick={handleUsernameSuggestionClose}
            variant="ghost-secondary"
            size="sm"
            className="username-suggestions__close__button"
          />
        </div>
        {errorMessage && (
          <Form.Control.Feedback type="invalid" hasIcon={false} className="form-text-size">
            {errorMessage}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    );
  }

  return (
    <FormGroup
      {...props}
      handleChange={handleOnChange}
      handleFocus={handleOnFocus}
      handleBlur={handleOnBlur}
    />
  );
};

UsernameField.defaultProps = {
  errorMessage: '',
  autoComplete: null,
  stackedLabel: false,
  floatingLabel: '',
};

UsernameField.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleErrorChange: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
  stackedLabel: PropTypes.bool,
  floatingLabel: PropTypes.string,
};

export default UsernameField;
